/**
 * Privacy utility functions for filtering profile data based on user privacy settings
 */

/**
 * Default privacy settings for users
 */
export const DEFAULT_PRIVACY_SETTINGS = {
  profile_visible: true,
  contact_info_visible: false,
  experience_visible: true,
  skills_visible: true,
  portfolio_visible: true
};

/**
 * Fields that should be hidden when contact_info_visible is false
 */
const CONTACT_INFO_FIELDS = [
  'email', 
  'phone', 
  'linkedin', 
  'website', 
  'github_url',
  'other_profiles'
];

/**
 * Fields that should be hidden when experience_visible is false
 */
const EXPERIENCE_FIELDS = [
  'work_experience',
  'experience_years', 
  'experience_level',
  'assessment_scores',
  'certifications',
  'years_of_experience_category'
];

/**
 * Fields that should be hidden when skills_visible is false
 */
const SKILLS_FIELDS = [
  'skills',
  'skills_text',
  'soft_skills',
  'industry_specialization'
];

/**
 * Fields that should be hidden when portfolio_visible is false
 */
const PORTFOLIO_FIELDS = [
  'portfolio_links',
  'portfolio_url', 
  'portfolio_description',
  'work_samples'
];

/**
 * Filter profile data based on privacy settings
 * @param {Object} profileData - The complete profile data
 * @param {Object} privacySettings - The user's privacy settings
 * @param {boolean} isOwnProfile - Whether the viewer is viewing their own profile
 * @returns {Object} Filtered profile data
 */
export function filterProfileByPrivacy(profileData, privacySettings = {}, isOwnProfile = false) {
  // If viewing own profile, return all data
  if (isOwnProfile) {
    return profileData;
  }

  // If no privacy settings, use defaults
  const settings = { ...DEFAULT_PRIVACY_SETTINGS, ...privacySettings };

  // If profile is not visible at all, return null
  if (!settings.profile_visible) {
    return null;
  }

  // Start with a copy of the profile data
  const filteredProfile = { ...profileData };

  // Filter contact information
  if (!settings.contact_info_visible) {
    CONTACT_INFO_FIELDS.forEach(field => {
      delete filteredProfile[field];
    });
  }

  // Filter experience information
  if (!settings.experience_visible) {
    EXPERIENCE_FIELDS.forEach(field => {
      delete filteredProfile[field];
    });
  }

  // Filter skills information
  if (!settings.skills_visible) {
    SKILLS_FIELDS.forEach(field => {
      delete filteredProfile[field];
    });
  }

  // Filter portfolio information
  if (!settings.portfolio_visible) {
    PORTFOLIO_FIELDS.forEach(field => {
      delete filteredProfile[field];
    });
  }

  return filteredProfile;
}

/**
 * Check if a specific profile section should be visible
 * @param {string} section - The section name ('contact', 'experience', 'skills', 'portfolio')
 * @param {Object} privacySettings - The user's privacy settings
 * @param {boolean} isOwnProfile - Whether the viewer is viewing their own profile
 * @returns {boolean} Whether the section should be visible
 */
export function isSectionVisible(section, privacySettings = {}, isOwnProfile = false) {
  if (isOwnProfile) return true;

  const settings = { ...DEFAULT_PRIVACY_SETTINGS, ...privacySettings };

  switch (section) {
    case 'profile':
      return settings.profile_visible;
    case 'contact':
      return settings.contact_info_visible;
    case 'experience':
      return settings.experience_visible;
    case 'skills':
      return settings.skills_visible;
    case 'portfolio':
      return settings.portfolio_visible;
    default:
      return true;
  }
}

/**
 * Get fields that should be selected from the database based on privacy settings
 * This helps optimize queries by only selecting visible fields
 * @param {Object} privacySettings - The user's privacy settings
 * @param {boolean} isOwnProfile - Whether the viewer is viewing their own profile
 * @returns {string[]} Array of field names to select
 */
export function getVisibleFields(privacySettings = {}, isOwnProfile = false) {
  // Base fields that are always visible
  const baseFields = [
    'id',
    'full_name',
    'bio',
    'location',
    'profile_picture',
    'public_username',
    'is_public_profile',
    'title',
    'availability_status',
    'timezone',
    'languages',
    'rating',
    'total_reviews',
    'communication_rating',
    'work_quality_rating',
    'created_at',
    'role',
    'privacy_settings'
  ];

  // If viewing own profile, return all fields
  if (isOwnProfile) {
    return baseFields.concat(CONTACT_INFO_FIELDS, EXPERIENCE_FIELDS, SKILLS_FIELDS, PORTFOLIO_FIELDS);
  }

  const settings = { ...DEFAULT_PRIVACY_SETTINGS, ...privacySettings };
  let visibleFields = [...baseFields];

  // Add fields based on privacy settings
  if (settings.contact_info_visible) {
    visibleFields = visibleFields.concat(CONTACT_INFO_FIELDS);
  }

  if (settings.experience_visible) {
    visibleFields = visibleFields.concat(EXPERIENCE_FIELDS);
  }

  if (settings.skills_visible) {
    visibleFields = visibleFields.concat(SKILLS_FIELDS);
  }

  if (settings.portfolio_visible) {
    visibleFields = visibleFields.concat(PORTFOLIO_FIELDS);
  }

  // Remove duplicates and return
  return [...new Set(visibleFields)];
}

/**
 * Check if the current user can view a profile
 * @param {Object} profileData - The profile data including privacy settings
 * @param {string} currentUserId - The current user's ID
 * @returns {boolean} Whether the profile can be viewed
 */
export function canViewProfile(profileData, currentUserId = null) {
  // Profile owner can always view their own profile
  if (currentUserId && profileData.id === currentUserId) {
    return true;
  }

  // Check if profile is public
  if (!profileData.is_public_profile) {
    return false;
  }

  // Check privacy settings
  const privacySettings = profileData.privacy_settings || {};
  return privacySettings.profile_visible !== false;
}